import { useState, useRef } from 'react';
import { ocrImage, type OCRResult } from '../lib/ocr';
import { Camera, X, Loader2, AlertTriangle } from 'lucide-react';

interface OCRUploadProps {
  type: 'drafts' | 'hydrostatic' | 'vessel';
  onResult: (result: OCRResult) => void;
  label?: string;
}

export default function OCRUpload({ type, onResult, label }: OCRUploadProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [previews, setPreviews] = useState<string[]>([]);
  const [files, setFiles] = useState<File[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFile = async (newFiles: File[]) => {
    setError('');
    const newPreviews: string[] = [];
    for (const file of newFiles) {
      const reader = new FileReader();
      await new Promise<void>((resolve) => {
        reader.onload = (e) => {
          newPreviews.push(e.target?.result as string);
          resolve();
        };
        reader.readAsDataURL(file);
      });
    }
    setPreviews((prev) => [...prev, ...newPreviews]);
    setFiles((prev) => [...prev, ...newFiles]);
  };

  const handleProcess = async () => {
    if (files.length === 0) return;
    setIsLoading(true);
    try {
      const result = await ocrImage(files, type);
      onResult(result);
      setPreviews([]);
      setFiles([]);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'OCR failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const droppedFiles = Array.from(e.dataTransfer.files).filter(f => f.type.startsWith('image/'));
    if (droppedFiles.length > 0) {
      handleFile(droppedFiles);
    }
  };

  const labels: Record<string, string> = {
    drafts: 'Upload draft marks photo',
    hydrostatic: 'Upload hydrostatic table pages',
    vessel: 'Upload vessel document photo',
  };

  return (
    <div className="space-y-3">
      {label && <p className="text-xs font-medium text-navy-400">{label}</p>}

      {previews.length === 0 ? (
        <div
          onDrop={handleDrop}
          onDragOver={(e) => e.preventDefault()}
          onClick={() => inputRef.current?.click()}
          className="border-2 border-dashed border-navy-700/50 rounded-xl p-6 text-center cursor-pointer hover:border-ocean-500/50 hover:bg-ocean-500/5 transition-all"
        >
          <Camera className="w-8 h-8 text-navy-500 mx-auto mb-2" />
          <p className="text-sm text-navy-300 font-medium">{labels[type]}</p>
          <p className="text-xs text-navy-500 mt-1">Drop images or click to browse (multiple pages supported)</p>
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={(e) => {
              const selected = Array.from(e.target.files || []).filter(f => f.type.startsWith('image/'));
              if (selected.length > 0) handleFile(selected);
            }}
          />
        </div>
      ) : (
        <div className="space-y-3">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {previews.map((preview, idx) => (
              <div key={idx} className="relative rounded-xl overflow-hidden border border-navy-700/50">
                <img src={preview} alt={`Page ${idx + 1}`} className="w-full h-24 object-contain bg-navy-900" />
                <span className="absolute top-1 left-1 bg-navy-800/80 text-navy-300 text-[10px] px-1.5 py-0.5 rounded">Page {idx + 1}</span>
                <button
                  onClick={() => {
                    setPreviews((prev) => prev.filter((_, i) => i !== idx));
                    setFiles((prev) => prev.filter((_, i) => i !== idx));
                  }}
                  className="absolute top-1 right-1 p-1 bg-navy-800/80 rounded hover:bg-navy-700 transition-colors"
                >
                  <X className="w-3 h-3 text-navy-300" />
                </button>
              </div>
            ))}
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleProcess}
              disabled={isLoading}
              className={`btn-primary flex-1 flex items-center justify-center gap-2 ${isLoading ? 'opacity-60 cursor-not-allowed' : ''}`}
            >
              {isLoading ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Processing {files.length} pages...</>
              ) : (
                <><Camera className="w-4 h-4" /> Extract Data from {files.length} Page{files.length > 1 ? 's' : ''}</>
              )}
            </button>
            <button
              onClick={() => { setPreviews([]); setFiles([]); setError(''); }}
              disabled={isLoading}
              className="btn-secondary px-4"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          {isLoading && (
            <div className="bg-ocean-500/10 border border-ocean-500/20 rounded-lg p-3 flex items-center gap-2">
              <Loader2 className="w-4 h-4 text-ocean-400 animate-spin" />
              <p className="text-xs text-ocean-400">Mistral AI is reading all pages and combining the data...</p>
            </div>
          )}
        </div>
      )}

      {error && (
        <div className="flex items-center gap-2 text-xs text-rose-400 bg-rose-500/10 rounded-lg p-2">
          <AlertTriangle className="w-4 h-4 flex-shrink-0" />
          {error}
        </div>
      )}
    </div>
  );
}
